import { useMemo } from "react";
import { isoToSec } from "../utils/period";

export interface CoverageSeg { from: string; to: string; n?: number }
export interface CoverageRow { key: string; label: string; color: string; segs: CoverageSeg[] }

const MONTHS_RU = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

// Horizontal timeline: one bar per source, filled where data exists. The axis
// spans the earliest to the latest data of all rows; month starts get ticks.
export default function CoverageBars({ rows, monthNames = MONTHS_RU, focusKeys }: {
  rows: CoverageRow[]; monthNames?: string[];
  /** Rows that set the axis range (e.g. telemetry only — the alarms journal goes back years). */
  focusKeys?: string[];
}) {
  const { lo, hi, months } = useMemo(() => {
    const focus = focusKeys ? rows.filter(r => focusKeys.includes(r.key)) : rows;
    const all = (focus.some(r => r.segs.length) ? focus : rows).flatMap(r => r.segs);
    if (!all.length) return { lo: 0, hi: 1, months: [] as { x: number; label: string }[] };
    const lo = Math.min(...all.map(s => isoToSec(s.from)));
    const hi = Math.max(...all.map(s => isoToSec(s.to)));
    const months: { x: number; label: string }[] = [];
    const d = new Date(lo * 1000);
    d.setUTCDate(1); d.setUTCHours(0, 0, 0, 0); d.setUTCMonth(d.getUTCMonth() + 1);
    while (d.getTime() / 1000 < hi) {
      months.push({ x: (d.getTime() / 1000 - lo) / (hi - lo) * 100, label: monthNames[d.getUTCMonth()] });
      d.setUTCMonth(d.getUTCMonth() + 1);
    }
    // keep the axis readable: at most ~6 month labels
    const every = Math.ceil(months.length / 6);
    // …and off the edge labels (the start/end dates sit there)
    return { lo, hi, months: months.filter((m, i) => i % every === 0 && m.x > 9 && m.x < 91) };
  }, [rows, monthNames, focusKeys]);
  const span = Math.max(1, hi - lo);
  const pct = (s: number) => Math.min(100, Math.max(0, (s - lo) / span * 100));
  const fmt = (s: number) => { const d = new Date(s * 1000); return `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}`; };
  return (
    <div style={{ fontSize: 11 }}>
      {rows.map(r => (
        <div key={r.key} style={{ display: "grid", gridTemplateColumns: "62px 1fr", gap: 6, alignItems: "center", margin: "4px 0" }}>
          <span style={{ color: "var(--hint)" }}>{r.label}</span>
          <div style={{ position: "relative", height: 10, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 3 }}>
            {months.map(m => <i key={m.label + m.x} style={{ position: "absolute", left: `${m.x}%`, top: 0, bottom: 0, borderLeft: "1px solid var(--border)" }} />)}
            {r.segs.map((s, i) => {
              const a = pct(isoToSec(s.from)), b = pct(isoToSec(s.to));
              if (isoToSec(s.to) < lo) return null;   // outside the (telemetry) axis range
              return <i key={i} style={{ position: "absolute", top: 0, bottom: 0, left: `${a}%`, width: `${Math.max(0.8, b - a)}%`, background: r.color, borderRadius: 3 }} />;
            })}
          </div>
        </div>
      ))}
      <div style={{ display: "grid", gridTemplateColumns: "62px 1fr", gap: 6 }}>
        <span />
        <div style={{ position: "relative", height: 14, color: "var(--hint)", fontSize: 10 }}>
          <span style={{ position: "absolute", left: 0 }}>{fmt(lo)}</span>
          {months.map(m => <span key={m.label + m.x} style={{ position: "absolute", left: `${m.x}%`, transform: "translateX(-50%)" }}>{m.label}</span>)}
          <span style={{ position: "absolute", right: 0 }}>{fmt(hi)}</span>
        </div>
      </div>
    </div>
  );
}
